import { useContext, useState } from 'react';
import './admin-access-modal.css';
import Button from '../../button/button';
import {
  EmployeesContext,
  UserContext,
  UserInfoContext,
} from '../../../context';
import Modal from '../../modal/modal';
import Avatar from '../../avatar/avatar';
import InputField from '../../input-field/input-field';

export default function AdminAccessModal({ isModalOpen, setIsModalOpen }) {
  const employees = useContext(EmployeesContext);
  const user = useContext(UserContext);
  const userInfo = useContext(UserInfoContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Deep clone employees to avoid mutating the original array so we can use it for submitting
  const employeesClone = JSON.parse(JSON.stringify(employees))
    .filter((employee) => employee.data.employmentStatus !== 'inactive')
    .sort((a, b) => {
      if (a.data.settings?.role === 'admin') return -1;
      if (b.data.settings?.role === 'admin') return 1;
      return 0;
    })
    .sort((a, b) => {
      if (a.id === userInfo?.companyData?.ownerUserId) return -1;
      if (b.id === userInfo?.companyData?.ownerUserId) return 1;
      return 0;
    });

  const handleChange = (e) => {
    const { name, checked } = e.target;
    const employeeId = name.split('-')[2];

    const employeeIndex = employeesClone.findIndex(
      (employee) => employee.id === employeeId
    );

    if (!employeesClone[employeeIndex].data.settings) {
      employeesClone[employeeIndex].data.settings = {
        role: checked ? 'admin' : '',
      };
    } else {
      employeesClone[employeeIndex].data.settings.role = checked ? 'admin' : '';
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage('');

    if (!confirm(
      'Er du sikker på at du vil ændre admin rettigheder og give adgang til at redigere og se informationer i hele HRfamly systemet?'
    )) {
      setIsSubmitting(false);
      return;
    }

    const employeesWithChangedRole = employeesClone.filter((employee) => {
      // Find the original employee from the unmodified employees array by ID
      const originalEmployee = employees.find(
        (original) => original.id === employee.id
      );

      const originalRole = originalEmployee?.data?.settings?.role || ''; // Defaults to empty string if undefined
      const updatedRole = employee?.data?.settings?.role || ''; // Defaults to empty string if undefined

      // Only include employees where the role has been modified
      return originalRole !== updatedRole;
    }).sort((a, b) => Number(a.id === user.uid) - Number(b.id === user.uid));

    // If no employees have had their role access changed, close the modal
    if (employeesWithChangedRole.length === 0) {
      setIsSubmitting(false);
      setIsModalOpen(false);
      return;
    }

    try {
      // Apply the actor's own demotion last, after the other role changes.
      for (const employee of employeesWithChangedRole) {
        const roleChanged = employee?.data?.settings?.role === 'admin';
        const token = await user.getIdToken(true);
        // Set the custom claim for the employee
        const setClaimResponse = await fetch('/api/user/setclaim', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: employee.id,
            companyId: userInfo.companyId,
            role: roleChanged ? 'admin' : 'employee',
          }),
        });

        const result = await setClaimResponse.json();

        if (!setClaimResponse.ok || result.error) {
          throw new Error('Error updating custom claim');
        }
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating:', error);
      setErrorMessage('Rettighederne kunne ikke gemmes. Prøv igen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      customWidth={600}
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      header={`Tildel admin adgange`}
      bottom={
        <div className="admin-access-modal-bottom">
          {errorMessage && <p role="alert">{errorMessage}</p>}
          <p>
            Medarbejdere med admin adgange er et tilkøb på 25 kr. om måneden pr.
            admin.
          </p>
          <Button disabled={isSubmitting} onClick={handleSubmit}>
            {isSubmitting ? 'Gemmer...' : 'Gem'}
          </Button>
        </div>
      }
      content={
        <div className="admin-access-modal-content">
          <p>
            Nedenfor ses et overblik over de medarbejdere der har admin adgang.
            Det betyder de kan redigere og se informationer i hele HRfamly
            systemet, også potentielt følsomme informationer.
          </p>
          {employeesClone.map((employee) => {
            const isAdmin = employee.data.settings?.role === 'admin';
            const isCompanyOwner =
              employee.id === userInfo?.companyData?.ownerUserId;

            return (
              <div
                key={employee.id}
                className="admin-access-modal-content-item"
              >
                <Avatar
                  firstName={employee.data.firstName}
                  lastName={employee.data.lastName}
                  profilePhotoUrl={employee.data.profilePhotoUrl}
                />
                <div className="admin-access-modal-content-item__inner">
                  <span className="admin-access-modal-content-item__name">
                    {employee.data.firstName} {employee.data.lastName}
                  </span>
                  <span className="admin-access-modal-content-item__position">
                    {employee.data.jobPosition}
                  </span>
                </div>
                {isCompanyOwner ? (
                  <InputField
                    label={`mobile-access-${employee.id}`}
                    key={`mobile-access-${employee.id}`}
                    type="switch"
                    defaultChecked={true}
                    className={`admin-access-modal-content-item__switch`}
                    disabled
                  />
                ) : (
                  <InputField
                    label={`mobile-access-${employee.id}`}
                    key={`mobile-access-${employee.id}`}
                    type="switch"
                    defaultChecked={isAdmin}
                    className={`admin-access-modal-content-item__switch`}
                    onChange={handleChange}
                  />
                )}
              </div>
            );
          })}
        </div>
      }
    />
  );
}
